/**
 * CustomChecklistRenderer
 *
 * Renders an inspection form from a `template_snapshot` (frozen at inspection creation time).
 * Supports all 8 answer types from the Builder, photo upload + per-photo annotation, smart
 * conditional visibility (visible_when), and flag computation against `flag_when` rules.
 *
 * Stats it reports back via `onStats` (so the parent can drive the "Sign & lock" CTA and the
 * Notice-of-Non-Compliance gate):
 *   { total, answered, unanswered, flagged }
 *
 * Props
 *   snapshot     – the frozen ChecklistTemplate stored on the inspection
 *   items        – { [qid]: { answer, notes, photos } }
 *   updateItem   – (qid, patch) => void
 *   onStats      – ({total,answered,unanswered,flagged}) => void
 *   locked       – bool
 */
import { useCallback, useEffect, useMemo, useState, memo } from "react";
import { useParams } from "react-router-dom";
import { toInput, fromInput } from "@/lib/datetime";
import { sanitizeRichHtml } from "@/lib/richText";
import { toast } from "sonner";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import CameraBurst from "@/components/CameraBurst";
import LazyImg from "@/components/LazyImg";
import { backupPhotosToDevice } from "@/lib/photoBackup";
import { downscaleImage } from "@/lib/checklist";
import { isLazyPhoto, resolveLazyPhoto } from "@/lib/lazyPhotos";
import { Camera, Trash2, Image as ImageIcon, Flag, Images } from "lucide-react";

const evalVisible = (rule, items) => {
  if (!rule || !rule.question_id || !rule.op) return true;
  const a = String((items[rule.question_id] || {}).answer ?? "");
  const t = String(rule.value ?? "");
  switch (rule.op) {
    case "equals": return a === t;
    case "not_equals": return a !== t;
    case "in": {
      const vals = Array.isArray(rule.value) ? rule.value : t.split(",").map((s) => s.trim()).filter(Boolean);
      return vals.map(String).includes(a);
    }
    case "gt": return Number(a) > Number(t);
    case "lt": return Number(a) < Number(t);
    default: return true;
  }
};

const evalFlag = (rule, answer) => {
  if (!rule || answer === "" || answer == null) return false;
  const a = String(answer).toLowerCase();
  const t = String(rule.value ?? "").toLowerCase();
  switch (rule.op) {
    case "equals": return a === t;
    case "not_equals": return a !== t;
    case "in":
    case "any_of": {
      const vals = Array.isArray(rule.value) ? rule.value : t.split(",").map((s) => s.trim()).filter(Boolean);
      return vals.map(String).includes(a);
    }
    case "gt": return Number(a) > Number(t);
    case "gte": return Number(a) >= Number(t);
    case "lt": return Number(a) < Number(t);
    case "lte": return Number(a) <= Number(t);
    default: return false;
  }
};

const CustomChecklistRenderer = ({ snapshot, items, updateItem, onStats, locked }) => {
  const [annotating, setAnnotating] = useState(null); // { qid, idx, src }
  const { id: inspRouteId } = useParams();

  // Compute stats whenever items / snapshot change
  const stats = useMemo(() => {
    let total = 0, answered = 0, flagged = 0;
    for (const sec of snapshot.sections || []) {
      for (const q of sec.questions || []) {
        if (!evalVisible(q.visible_when, items)) continue;
        if (!q.flag_when) continue; // only flag-bearing questions count toward compliance
        total += 1;
        const ans = (items[q.id] || {}).answer;
        if (ans !== "" && ans != null) {
          answered += 1;
          if (evalFlag(q.flag_when, ans)) flagged += 1;
        }
      }
    }
    return { total, answered, unanswered: total - answered, flagged };
  }, [items, snapshot]);

  useEffect(() => { onStats && onStats(stats); }, [stats, onStats]);

  const onAnnotate = useCallback(async (qid, idx, src) => {
    if (isLazyPhoto(src)) {
      try { src = await resolveLazyPhoto(inspRouteId, src); }
      catch { toast.error("Photo not available offline yet"); return; }
    }
    setAnnotating({ qid, idx, src });
  }, [inspRouteId]);

  return (
    <div className="space-y-6" data-testid="custom-checklist">
      {snapshot.sections?.map((sec) => {
        const visible = (sec.questions || []).filter((q) => evalVisible(q.visible_when, items));
        if (!visible.length) return null;
        return (
          <section key={sec.id} className="bg-white border-2 border-slate-200 p-5 sm:p-6" data-testid={`section-${sec.id}`}>
            <h2 className="text-lg font-black uppercase tracking-wide text-slate-900 mb-4">{sec.title}</h2>
            <div className="space-y-5">
              {visible.map((q) => (
                <QuestionField
                  key={q.id}
                  q={q}
                  value={items[q.id] || { answer: "", notes: "", photos: [] }}
                  flagged={evalFlag(q.flag_when, (items[q.id] || {}).answer)}
                  disabled={locked}
                  updateItem={updateItem}
                  onAnnotate={onAnnotate}
                />
              ))}
            </div>
          </section>
        );
      })}

      {annotating && (
        <PhotoAnnotator
          src={annotating.src}
          onCancel={() => setAnnotating(null)}
          onSave={(annotated) => {
            const cur = (items[annotating.qid] || {}).photos || [];
            const next = cur.slice();
            next[annotating.idx] = annotated;
            updateItem(annotating.qid, { photos: next });
            setAnnotating(null);
          }}
        />
      )}
    </div>
  );
};

// Memoised — only the question being edited re-renders per keystroke.
const QuestionField = memo(({ q, value, flagged, disabled, updateItem, onAnnotate }) => {
  const onChange = (patch) => updateItem(q.id, patch);
  const onUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Downscale client-side so a 10MB phone photo becomes ~200KB before storing
    const dataUrls = await Promise.all(files.map((f) => downscaleImage(f)));
    updateItem(q.id, { photos: [...(value.photos || []), ...dataUrls] });
  };
  const onAppend = (dataUrls) => {
    if (!Array.isArray(dataUrls) || !dataUrls.length) return;
    backupPhotosToDevice(dataUrls, q.label || q.id);
    updateItem(q.id, { photos: [...(value.photos || []), ...dataUrls] });
  };
  const onRemovePhoto = (idx) => updateItem(q.id, { photos: (value.photos || []).filter((_, i) => i !== idx) });

  const tone = flagged ? "border-l-red-500" : value.answer ? "border-l-emerald-500" : "border-l-slate-200";
  const photoCount = (value.photos || []).length;
  const needsMore = q.photo_required && photoCount < (q.photo_min ?? 1);
  const [burstOpen, setBurstOpen] = useState(false);
  const remaining = Math.max(0, (q.photo_max ?? 10) - photoCount);

  return (
    <div id={`q-${q.id}`} className={`pl-4 border-l-4 ${tone}`} data-testid={`field-${q.id}`}>
      <p className="text-sm font-semibold text-slate-900 leading-snug">
        {q.label_html ? (
          <span
            className="[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_b]:font-black [&_strong]:font-black"
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(q.label_html) }}
          />
        ) : (
          q.label
        )}
        {q.clause && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">({q.clause})</span>}
        {flagged && <Flag size={12} strokeWidth={3} className="inline ml-2 text-red-600" aria-label="Non-compliant"/>}
      </p>
      {(q.help_html || q.help) && (q.help_html ? (
        <div
          className="text-xs text-slate-500 mt-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_b]:font-bold [&_strong]:font-bold"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(q.help_html) }}
        />
      ) : (
        <p className="text-xs text-slate-500 mt-1">{q.help}</p>
      ))}

      <div className="mt-3">
        <AnswerInput q={q} value={value} disabled={disabled} onChange={onChange}/>
      </div>

      {q.answer_type === "fire_compliance" && value.answer === "Non-Compliant" && (
        <DefectPanel q={q} value={value} disabled={disabled} onChange={onChange}/>
      )}

      <label className="block mt-3">
        <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Inspector notes</span>
        <textarea
          data-testid={`field-notes-${q.id}`}
          rows={5}
          value={value.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          disabled={disabled}
          className="w-full p-2 text-sm border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none disabled:opacity-60 bg-white text-slate-900"
        />
      </label>

      {/* Photos */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 inline-flex items-center gap-1">
            <ImageIcon size={11} strokeWidth={3}/>Photos ({photoCount}{q.photo_max ? `/${q.photo_max}` : ""})
            {q.photo_required && <span className="text-amber-700">· required</span>}
          </span>
          {!disabled && remaining > 0 && (
            <div className="flex items-center gap-1.5">
              {/* Burst camera — take multiple shots in one session */}
              <button
                type="button"
                onClick={() => setBurstOpen(true)}
                data-testid={`field-photo-camera-${q.id}`}
                className="inline-flex items-center gap-1 h-8 px-2.5 bg-[#1E1B4B] text-white text-xs font-bold hover:bg-[#0F0E2E]">
                <Camera size={13} strokeWidth={2.5}/>Camera
              </button>
              {/* Gallery / file picker — multi-select from photo library */}
              <label
                className="cursor-pointer inline-flex items-center gap-1 h-8 px-2.5 bg-white border-2 border-slate-300 text-slate-900 text-xs font-bold hover:border-slate-900"
                data-testid={`field-photo-gallery-${q.id}`}>
                <Images size={13} strokeWidth={2.5}/>Gallery
                <input
                  type="file" accept="image/*" multiple
                  className="hidden"
                  data-testid={`field-photo-input-${q.id}`}
                  onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
                />
              </label>
            </div>
          )}
        </div>
        {q.photo_hint && <p className="text-[11px] text-slate-500 italic mb-2">{q.photo_hint}</p>}
        {needsMore && <p className="text-[11px] text-amber-700 font-semibold mb-2">Add at least {q.photo_min} photo{q.photo_min === 1 ? "" : "s"} before locking.</p>}
        {photoCount > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {(value.photos || []).map((src, i) => (
              <div key={i} className="relative group" data-testid={`field-photo-${q.id}-${i}`}>
                <LazyImg src={src} alt={`evidence ${i+1}`} className="w-full h-24 object-cover border border-slate-200" />
                {!disabled && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                    <button onClick={() => onAnnotate(q.id, i, src)} title="Annotate"
                      className="h-8 px-2 bg-white text-slate-900 text-xs font-bold">Edit</button>
                    <button onClick={() => onRemovePhoto(i)} title="Remove"
                      className="h-8 w-8 bg-red-600 text-white inline-flex items-center justify-center"><Trash2 size={14}/></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Burst-camera modal — renders full-screen when open */}
      <CameraBurst
        open={burstOpen}
        remaining={remaining}
        onClose={() => setBurstOpen(false)}
        onDone={(dataUrls) => {
          setBurstOpen(false);
          onAppend && onAppend(dataUrls);
        }}
      />
    </div>
  );
});

const FIRE_OPTIONS = [
  ["Compliant", "bg-emerald-600 text-white border-emerald-600"],
  ["Non-Compliant", "bg-red-600 text-white border-red-600"],
  ["Further Investigation Required", "bg-orange-500 text-white border-orange-500"],
  ["Not Applicable", "bg-slate-500 text-white border-slate-500"],
  ["Unable to Inspect", "bg-amber-500 text-white border-amber-500"],
];

// Defect capture — opens automatically when a fire question is Non-Compliant.
const DefectPanel = ({ q, value, disabled, onChange }) => {
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  const inp = "mt-1 w-full border-2 border-red-200 focus:border-red-500 focus:outline-none px-2 py-1.5 text-sm bg-white text-slate-900 disabled:opacity-60";
  const lbl = "text-[11px] font-bold uppercase tracking-wide text-red-800";
  return (
    <div className="mt-3 border-2 border-red-300 bg-red-50 p-3 space-y-2" data-testid={`defect-panel-${q.id}`}>
      <p className="text-xs font-black uppercase tracking-wide text-red-700">Non-compliance details — attach photographs above</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="block"><span className={lbl}>Location of defect</span>
          <input className={inp} disabled={disabled} value={value.defect_location || ""} onChange={set("defect_location")} data-testid={`defect-location-${q.id}`}/></label>
        <label className="block"><span className={lbl}>Legislative / FSS / NCC / standard reference</span>
          <input className={inp} disabled={disabled} value={value.defect_reference || ""} onChange={set("defect_reference")} placeholder={q.clause || ""}/></label>
      </div>
      <label className="block"><span className={lbl}>Detailed defect description</span>
        <textarea rows={3} className={inp} disabled={disabled} value={value.defect_description || ""} onChange={set("defect_description")} data-testid={`defect-description-${q.id}`}/></label>
      <label className="block"><span className={lbl}>Required rectification</span>
        <textarea rows={2} className={inp} disabled={disabled} value={value.defect_rectification || ""} onChange={set("defect_rectification")}/></label>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="block"><span className={lbl}>Risk level</span>
          <select className={inp} disabled={disabled} value={value.defect_risk || ""} onChange={set("defect_risk")} data-testid={`defect-risk-${q.id}`}>
            <option value="">— Select —</option>
            {["Low", "Medium", "High", "Critical / Urgent"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select></label>
        <label className="block"><span className={lbl}>Recommended timeframe</span>
          <select className={inp} disabled={disabled} value={value.defect_timeframe || ""} onChange={set("defect_timeframe")}>
            <option value="">— Select —</option>
            {["Immediately", "24 hours", "7 days", "14 days", "30 days", "60 days", "90 days"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
      </div>
      <label className="block"><span className={lbl}>Officer notes</span>
        <textarea rows={2} className={inp} disabled={disabled} value={value.defect_officer_notes || ""} onChange={set("defect_officer_notes")}/></label>
    </div>
  );
};

const AnswerInput = ({ q, value, disabled, onChange }) => {
  const set = (answer) => onChange({ answer });
  const tid = `field-answer-${q.id}`;

  if (q.answer_type === "fire_compliance") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={tid}>
        {FIRE_OPTIONS.map(([val, activeCls]) => (
          <button key={val} disabled={disabled} onClick={() => set(val)}
            data-testid={`${tid}-${val.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`h-10 px-3 font-bold text-xs sm:text-sm border-2 transition ${value.answer === val ? activeCls : "bg-white text-slate-900 border-slate-300 hover:border-slate-900"} disabled:opacity-60`}>
            {val}
          </button>
        ))}
      </div>
    );
  }
  if (q.answer_type === "yes_no" || q.answer_type === "yes_no_na") {
    const choices = q.answer_type === "yes_no" ? [["yes","Yes"],["no","No"]] : [["yes","Yes"],["no","No"],["na","N/A"]];
    return (
      <div className="flex gap-2" data-testid={tid}>
        {choices.map(([val, lbl]) => (
          <button key={val} disabled={disabled} onClick={() => set(val)} data-testid={`${tid}-${val}`}
            className={`h-10 px-4 font-bold text-sm border-2 transition ${value.answer === val ? (val === "yes" ? "bg-emerald-600 text-white border-emerald-600" : val === "no" ? "bg-red-600 text-white border-red-600" : "bg-slate-700 text-white border-slate-700") : "bg-white text-slate-900 border-slate-300 hover:border-slate-900"} disabled:opacity-60`}>
            {lbl}
          </button>
        ))}
      </div>
    );
  }
  if (q.answer_type === "multi_choice") {
    return (
      <select data-testid={tid} value={value.answer || ""} disabled={disabled} onChange={(e) => set(e.target.value)}
        className="h-11 w-full max-w-md px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none disabled:opacity-60 bg-white text-slate-900">
        <option value="">— Select —</option>
        {(q.choices || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  if (q.answer_type === "numeric") {
    return <input type="number" data-testid={tid} value={value.answer || ""} disabled={disabled}
      onChange={(e) => set(e.target.value)} className="h-11 w-full max-w-xs px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none disabled:opacity-60 bg-white text-slate-900"/>;
  }
  if (q.answer_type === "long_text") {
    return <textarea data-testid={tid} rows={5} value={value.answer || ""} disabled={disabled}
      onChange={(e) => set(e.target.value)} className="w-full p-2 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none text-sm disabled:opacity-60 bg-white text-slate-900"/>;
  }
  if (q.answer_type === "date") {
    return <input type="datetime-local" data-testid={tid} value={toInput(value.answer, "datetime")} disabled={disabled}
      onChange={(e) => set(fromInput(e.target.value, "datetime"))} className="h-11 w-full max-w-xs px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none disabled:opacity-60 bg-white text-slate-900"/>;
  }
  if (q.answer_type === "signature") {
    return value.answer
      ? <img src={value.answer} alt="signature" className="h-20 border-2 border-slate-200 bg-white" data-testid={tid}/>
      : <p className="text-xs text-slate-500 italic">Use the Sign &amp; lock flow at the bottom of the page to capture your signature.</p>;
  }
  // default: short text
  return <input data-testid={tid} value={value.answer || ""} disabled={disabled}
    onChange={(e) => set(e.target.value)} className="h-11 w-full max-w-md px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none disabled:opacity-60 bg-white text-slate-900"/>;
};

export default CustomChecklistRenderer;
